import { Toaster, ToastReference } from '@economic/taco';
import { t } from '@lingui/macro';
import * as R from 'ramda';
import React from 'react';
import internalApiClient from '../../../shared/util/api/internalApiClient';

export const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/gif', 'image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 9961472;
/**
 * Multiple files are uploaded in batches of 10.
 */
const UPLOAD_BATCH_SIZE = 10;

const wait = async (ms: number) =>
    new Promise(resolve => {
        setTimeout(resolve, ms);
    });

const validate = (files: FileList): [validFiles: File[], errors: string[]] => {
    const valid: File[] = [];
    const errors: string[] = [];

    Array.from<File>(files).forEach(file => {
        if (ACCEPTED_FILE_TYPES.includes(file.type)) {
            valid.push(file);
        } else if (file.type !== 'text/plain' && file.type !== 'text/html') {
            const fileName = file.name;
            if (file.size > MAX_FILE_SIZE) {
                errors.push(t`${fileName} - the file is too big.`);
            } else {
                errors.push(
                    t`${fileName} - the file type is not supported. You can only attach PDFs or image files (JPG, PNG and GIF)`
                );
            }
        }
    });

    return [valid, errors];
};

function showErrors(errors: string[], toast?: ToastReference) {
    const toastContent = (
        <ul>
            {errors.map((error, i) => (
                <li key={i}> {error} </li>
            ))}
        </ul>
    );

    toast?.error(toastContent);
}

export interface UploadedDocument {
    documentNumber: number;
    name: string;
}

export enum DocumentSourceType {
    JournalUpload = 11,
}

export async function upload(
    files: FileList,
    toast?: Toaster<ToastReference>,
    onUpload?: (documentNumber: number) => Promise<void>,
    sourceType: DocumentSourceType = DocumentSourceType.JournalUpload
): Promise<UploadedDocument[]> {
    const [validFiles, errors] = validate(files);
    const uploaded: UploadedDocument[] = [];

    let toastRef: ToastReference | undefined;
    if (validFiles.length) {
        toastRef = toast?.loading(t`Uploading document(s)...`);

        const fileBatches = R.splitEvery(UPLOAD_BATCH_SIZE, validFiles);

        while (fileBatches.length > 0) {
            const fileBatch: File[] | undefined = fileBatches.shift();

            if (!fileBatch) {
                break;
            }

            await Promise.all(
                fileBatch.map(async (file: File) => {
                    try {
                        const data = new FormData();
                        data.append('files[]', file, file.name);
                        data.append('source', sourceType.toString());
                        const { data: responseData } = await internalApiClient.post('/inbox/documents', data);
                        if (responseData) {
                            uploaded.push({ documentNumber: responseData.documentNumber, name: responseData.name });
                            await onUpload?.(responseData?.documentNumber);
                        }
                    } catch (error) {
                        const message = error.response?.data?.message || error.message;
                        errors.push(`${file.name} - ${message}`);
                    }
                })
            );

            if (fileBatches.length > 0) {
                await wait(250);
            }
        }

        if (uploaded.length) {
            toastRef?.success(<>{t`${uploaded.length} document(s) uploaded.`}</>);
        }

        if (errors.length) {
            showErrors(errors, toastRef);
        }
    }

    return uploaded;
}
